import { TaskAltOutlined } from "@mui/icons-material";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import colors from "../../../config/colors";
import useSupervisorQueue from "../controllers/use-supervisor-queue";
import {
	EnabledFeatures,
	SupervisorQueueItem,
} from "../store/supervisor-home-types";
import ActionQueueGroup from "./action-queue-group";

interface ActionQueueProps {
	queue?: SupervisorQueueItem[];
	enabledFeatures?: EnabledFeatures;
	isLoading: boolean;
}

function ActionQueue({ queue, enabledFeatures, isLoading }: ActionQueueProps) {
	const { t } = useTranslation();
	const { groups, totalCount, isEmpty } = useSupervisorQueue(
		queue,
		enabledFeatures
	);

	return (
		<Box sx={{ minWidth: 0 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					mb: 1.5,
				}}
			>
				<Typography
					component='h2'
					sx={{ fontSize: "1rem", fontWeight: 500 }}
				>
					{t("supervisor-home_action-queue")}
				</Typography>
				{!isLoading ? (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
						<Typography sx={{ fontSize: "12px", color: colors.gray }}>
							{t("supervisor-home_queue-summary", { count: totalCount })}
						</Typography>
						{totalCount > 0 ? (
							<Chip
								label={t("supervisor-home_urgent-first")}
								size='small'
								sx={{
									backgroundColor: colors.grey,
									color: colors.title,
									fontSize: "0.75rem",
								}}
							/>
						) : null}
					</Box>
				) : null}
			</Box>

			{isLoading ? (
				<Skeleton variant='rounded' height={220} sx={{ borderRadius: "10px" }} />
			) : null}

			{!isLoading && isEmpty ? (
				<Box
					sx={{
						border: `1px solid ${colors.border}`,
						borderRadius: "10px",
						padding: "56px 24px",
						textAlign: "center",
					}}
				>
					<TaskAltOutlined sx={{ fontSize: "40px", color: colors.green }} />
					<Typography sx={{ fontSize: "1rem", fontWeight: 500, mt: 1.25 }}>
						{t("supervisor-home_queue-empty-title")}
					</Typography>
					<Typography sx={{ fontSize: "14px", color: colors.gray, mt: 0.75 }}>
						{t("supervisor-home_queue-empty-description")}
					</Typography>
				</Box>
			) : null}

			{!isLoading
				? groups.map((group) => (
					<ActionQueueGroup key={group.type} group={group} />
				))
				: null}
		</Box>
	);
}

export default ActionQueue;
